import { useState } from 'react';
import { Mail, MapPin, Phone, Loader2 } from 'lucide-react';
import { useSettings } from '@/context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [sending, setSending] = useState(false);
  const { settings } = useSettings();

  const contact = settings?.contact || {};
  const primaryColor = settings?.theme?.primaryColor || '#1e3a5f';
  const contactPage = settings?.contactPage || {};

  // Helper function to convert Google Maps URL to embed URL
  const getMapEmbedUrl = (url) => {
    if (!url) return null;
    
    if (url.includes('/maps/embed')) {
      return url;
    }
    
    try {
      const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+),(\d+)/);
      if (coordMatch) {
        const [, lat, lng] = coordMatch;
        return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM!5e0!3m2!1ses!2sar!4v1234567890123!5m2!1ses!2sar`;
      }
    } catch (e) {
      console.error('Error parsing map URL:', e);
    }
    
    return null;
  };

  const mapEmbedUrl = getMapEmbedUrl(contact.mapEmbed);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nombre || !email || !mensaje) {
      toast.error('Por favor, completá todos los campos');
      return;
    }

    setSending(true);
    
    try {
      await axios.post(`${API_BASE}/api/contact`, {
        nombre,
        email,
        mensaje
      });
      
      toast.success('¡Mensaje enviado! Te responderemos a la brevedad.');
      setNombre('');
      setEmail('');
      setMensaje('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Error al enviar el mensaje. Por favor, intentá de nuevo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="container-safe py-14 md:py-20">
      <div className="mx-auto max-w-3xl text-center">
        <div className="text-xs font-semibold tracking-widest text-slate-500">CONTACTO</div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
          {contactPage.title || 'Coordinemos una llamada'}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          {contactPage.subtitle || 'Enviá tu consulta. También podés escribir directo por WhatsApp desde el botón flotante.'}
        </p>
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-12">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft md:col-span-7">
          <div className="text-sm font-semibold text-slate-900">{contactPage.formTitle || 'Formulario'}</div>

          <form onSubmit={handleSubmit} className="mt-5 grid gap-4">
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-slate-700">{contactPage.nameLabel || 'Nombre'}</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-900 transition-colors"
                placeholder={`Tu ${contactPage.nameLabel?.toLowerCase() || 'nombre'}`}
                data-testid="contact-name-input"
              />
            </label>

            <label className="grid gap-2 text-sm">
              <span className="font-medium text-slate-700">{contactPage.emailLabel || 'Email'}</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-900 transition-colors"
                placeholder={`tu@${contactPage.emailLabel?.toLowerCase() || 'email'}.com`}
                type="email"
                data-testid="contact-email-input"
              />
            </label>

            <label className="grid gap-2 text-sm">
              <span className="font-medium text-slate-700">{contactPage.messageLabel || 'Mensaje'}</span>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                className="min-h-[140px] rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-900 transition-colors"
                placeholder={contactPage.messagePlaceholder || 'Contanos qué necesitás (ubicación, m², tipo de operación, plazos).'}
                data-testid="contact-message-input"
              />
            </label>

            <button
              type="submit"
              disabled={sending}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-soft hover:opacity-95 transition-opacity disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
              data-testid="contact-submit-btn"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {sending ? 'Enviando...' : (contactPage.submitButton || 'Enviar consulta')}
            </button>

            <div className="text-xs text-slate-500">
              * Este formulario abre tu cliente de email con la información pre-completada.
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft md:col-span-5">
          <div className="text-sm font-semibold text-slate-900">{contactPage.dataTitle || 'Datos'}</div>

          <div className="mt-5 grid gap-4 text-sm text-slate-700">
            <div className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5" />
              <div>
                <div className="font-medium">Ubicación</div>
                <div className="text-slate-600">{contact.locationLine1 || contact.location}</div>
                {contact.locationLine2 && <div className="text-slate-600">{contact.locationLine2}</div>}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail size={18} className="mt-0.5" />
              <div>
                <div className="font-medium">Email</div>
                <a
                  className="text-slate-600 underline decoration-slate-300 hover:decoration-slate-900"
                  href={`mailto:${contact.email}`}
                >
                  {contact.email}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Phone size={18} className="mt-0.5" />
              <div>
                <div className="font-medium">WhatsApp</div>
                <a
                  className="text-slate-600 underline decoration-slate-300 hover:decoration-slate-900"
                  href={`https://wa.me/${contact.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  +{contact.whatsapp?.replace(/^(\d{2})(\d{2})(\d{2})(\d+)$/, '$1 $2 $3 $4')}
                </a>
              </div>
            </div>
          </div>

          {mapEmbedUrl && (
            <>
              <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <iframe
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={mapEmbedUrl}
                  title="Ubicación"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};
